/**
 * Chain registry for PLECO Bubblemap.
 *
 * Every chain we support is declared here once. The API uses `rpcDefaults` /
 * `explorer` for indexing + linking, the web app uses `label`, `color` and
 * `symbol` for presentation. Adding a chain = adding an entry here.
 */
const evmExplorer = (name, base) => ({
    name,
    base,
    address: (a) => `${base}/address/${a}`,
    tx: (h) => `${base}/tx/${h}`,
    token: (a) => `${base}/token/${a}`,
});
const EVM_BURN = [
    '0x0000000000000000000000000000000000000000',
    '0x000000000000000000000000000000000000dead',
    '0x0000000000000000000000000000000000000001',
];
export const CHAINS = {
    eth: {
        id: 'eth',
        family: 'evm',
        chainId: 1,
        label: 'Ethereum',
        symbol: 'ETH',
        decimals: 18,
        color: '#7b8cff',
        rpcDefaults: ['https://eth.llamarpc.com', 'https://rpc.ankr.com/eth', 'https://ethereum-rpc.publicnode.com'],
        explorer: evmExplorer('Etherscan', 'https://etherscan.io'),
        indexable: true,
        burnAddresses: EVM_BURN,
    },
    bsc: {
        id: 'bsc',
        family: 'evm',
        chainId: 56,
        label: 'BNB Chain',
        symbol: 'BNB',
        decimals: 18,
        color: '#f0b90b',
        rpcDefaults: ['https://bsc-dataseed.bnbchain.org', 'https://bsc-rpc.publicnode.com'],
        explorer: evmExplorer('BscScan', 'https://bscscan.com'),
        indexable: true,
        burnAddresses: EVM_BURN,
    },
    base: {
        id: 'base',
        family: 'evm',
        chainId: 8453,
        label: 'Base',
        symbol: 'ETH',
        decimals: 18,
        color: '#2f6bff',
        rpcDefaults: ['https://mainnet.base.org', 'https://base-rpc.publicnode.com'],
        explorer: evmExplorer('BaseScan', 'https://basescan.org'),
        indexable: true,
        burnAddresses: EVM_BURN,
    },
    arb: {
        id: 'arb',
        family: 'evm',
        chainId: 42161,
        label: 'Arbitrum',
        symbol: 'ETH',
        decimals: 18,
        color: '#28a0f0',
        rpcDefaults: ['https://arb1.arbitrum.io/rpc', 'https://arbitrum-one-rpc.publicnode.com'],
        explorer: evmExplorer('Arbiscan', 'https://arbiscan.io'),
        indexable: true,
        burnAddresses: EVM_BURN,
    },
    poly: {
        id: 'poly',
        family: 'evm',
        chainId: 137,
        label: 'Polygon',
        symbol: 'POL',
        decimals: 18,
        color: '#8247e5',
        rpcDefaults: ['https://polygon-rpc.com', 'https://polygon-bor-rpc.publicnode.com'],
        explorer: evmExplorer('PolygonScan', 'https://polygonscan.com'),
        indexable: true,
        burnAddresses: EVM_BURN,
    },
    avax: {
        id: 'avax',
        family: 'evm',
        chainId: 43114,
        label: 'Avalanche',
        symbol: 'AVAX',
        decimals: 18,
        color: '#e84142',
        rpcDefaults: ['https://api.avax.network/ext/bc/C/rpc', 'https://avalanche-c-chain-rpc.publicnode.com'],
        explorer: evmExplorer('Snowtrace', 'https://snowtrace.io'),
        indexable: true,
        burnAddresses: EVM_BURN,
    },
    op: {
        id: 'op',
        family: 'evm',
        chainId: 10,
        label: 'Optimism',
        symbol: 'ETH',
        decimals: 18,
        color: '#ff0420',
        rpcDefaults: ['https://mainnet.optimism.io', 'https://optimism-rpc.publicnode.com'],
        explorer: evmExplorer('Optimistic Etherscan', 'https://optimistic.etherscan.io'),
        indexable: true,
        burnAddresses: EVM_BURN,
    },
    sonic: {
        id: 'sonic',
        family: 'evm',
        chainId: 146,
        label: 'Sonic',
        symbol: 'S',
        decimals: 18,
        color: '#f7e7c4',
        rpcDefaults: ['https://rpc.soniclabs.com'],
        explorer: evmExplorer('SonicScan', 'https://sonicscan.org'),
        indexable: true,
        burnAddresses: EVM_BURN,
    },
    hype: {
        id: 'hype',
        family: 'evm',
        chainId: 999,
        label: 'HyperEVM',
        symbol: 'HYPE',
        decimals: 18,
        color: '#97fce4',
        rpcDefaults: ['https://rpc.hyperliquid.xyz/evm'],
        explorer: evmExplorer('HyperEVM Scan', 'https://hyperevmscan.io'),
        indexable: true,
        burnAddresses: EVM_BURN,
    },
    monad: {
        id: 'monad',
        family: 'evm',
        chainId: 143,
        label: 'Monad',
        symbol: 'MON',
        decimals: 18,
        color: '#836ef9',
        rpcDefaults: ['https://rpc.monad.xyz'],
        explorer: evmExplorer('MonadScan', 'https://monadscan.com'),
        indexable: true,
        burnAddresses: EVM_BURN,
    },
    sol: {
        id: 'sol',
        family: 'svm',
        label: 'Solana',
        symbol: 'SOL',
        decimals: 9,
        color: '#14f195',
        // Public mainnet-beta refuses indexed calls; set PLECO_HELIUS_KEY or PLECO_RPC_SOL.
        rpcDefaults: ['https://api.mainnet-beta.solana.com'],
        explorer: {
            name: 'Solscan',
            base: 'https://solscan.io',
            address: (a) => `https://solscan.io/account/${a}`,
            tx: (h) => `https://solscan.io/tx/${h}`,
            token: (a) => `https://solscan.io/token/${a}`,
        },
        indexable: true,
        burnAddresses: ['11111111111111111111111111111111', '1nc1nerator11111111111111111111111111111111'],
    },
    ton: {
        id: 'ton',
        family: 'other',
        label: 'TON',
        symbol: 'TON',
        decimals: 9,
        color: '#0098ea',
        rpcDefaults: ['https://toncenter.com/api/v2/jsonRPC'],
        explorer: {
            name: 'Tonviewer',
            base: 'https://tonviewer.com',
            address: (a) => `https://tonviewer.com/${a}`,
            tx: (h) => `https://tonviewer.com/transaction/${h}`,
            token: (a) => `https://tonviewer.com/${a}`,
        },
        indexable: false,
        burnAddresses: [],
    },
    tron: {
        id: 'tron',
        family: 'tvm',
        label: 'Tron',
        symbol: 'TRX',
        decimals: 6,
        color: '#ff060a',
        rpcDefaults: ['https://api.trongrid.io'],
        explorer: {
            name: 'Tronscan',
            base: 'https://tronscan.org',
            address: (a) => `https://tronscan.org/#/address/${a}`,
            tx: (h) => `https://tronscan.org/#/transaction/${h}`,
            token: (a) => `https://tronscan.org/#/token20/${a}`,
        },
        indexable: false,
        burnAddresses: [],
    },
    aptos: {
        id: 'aptos',
        family: 'move',
        label: 'Aptos',
        symbol: 'APT',
        decimals: 8,
        color: '#06f7b2',
        rpcDefaults: ['https://fullnode.mainnet.aptoslabs.com/v1'],
        explorer: {
            name: 'Aptos Explorer',
            base: 'https://explorer.aptoslabs.com',
            address: (a) => `https://explorer.aptoslabs.com/account/${a}`,
            tx: (h) => `https://explorer.aptoslabs.com/txn/${h}`,
            token: (a) => `https://explorer.aptoslabs.com/coin/${a}`,
        },
        indexable: false,
        burnAddresses: [],
    },
};
export const CHAIN_IDS = Object.keys(CHAINS);
export const isChainId = (v) => Object.prototype.hasOwnProperty.call(CHAINS, v);
export const getChain = (id) => CHAINS[id];
/** DexScreener uses its own chain slugs; map ours onto theirs. */
export const DEXSCREENER_SLUG = {
    eth: 'ethereum',
    bsc: 'bsc',
    base: 'base',
    arb: 'arbitrum',
    poly: 'polygon',
    avax: 'avalanche',
    op: 'optimism',
    sonic: 'sonic',
    hype: 'hyperliquid',
    monad: 'monad',
    sol: 'solana',
    ton: 'ton',
    tron: 'tron',
    aptos: 'aptos',
};
/** Normalises an address for storage/comparison (EVM => lowercase). */
export function normalizeAddress(chain, address) {
    const cfg = CHAINS[chain];
    const trimmed = address.trim();
    return cfg?.family === 'evm' ? trimmed.toLowerCase() : trimmed;
}
export function isValidAddress(chain, address) {
    const cfg = CHAINS[chain];
    if (!cfg)
        return false;
    const a = address.trim();
    switch (cfg.family) {
        case 'evm':
            return /^0x[0-9a-fA-F]{40}$/.test(a);
        case 'svm':
            return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(a);
        default:
            return a.length >= 10;
    }
}
//# sourceMappingURL=chains.js.map